/**
 * Logger client-side: accoda gli eventi in localStorage e li invia al BE
 * (POST /api/client-errors/batch), così sopravvivono a reload e assenza di rete.
 * Di solito si usa tramite `safeAction`. Non blocca mai la UI: se l'invio
 * fallisce le righe restano in coda per il flush successivo.
 */
import api from './api';

const STORAGE_KEY = 'clientErrorLog:queue';
const MAX_QUEUE_SIZE = 100;
const FLUSH_DEBOUNCE_MS = 5_000;
const FLUSH_INTERVAL_MS = 15_000;

export type ClientErrorOutcome = 'ATTEMPTED' | 'OK' | 'FAIL';

export interface ClientErrorEntry {
    targetAction: string;          // es. INCARICO_START, TURNO_STOP, REPORT_INVIATO
    outcome: ClientErrorOutcome;   // ATTEMPTED (prima della call) / OK / FAIL (dopo)
    attemptId: string;             // collega ATTEMPTED ↔ OK/FAIL della stessa azione
    entityType?: string;           // incarico | turno | relazione
    entityId?: number | string;
    httpStatus?: number | null;    // null = network error / timeout
    errorMsg?: string;
    payload?: any;                 // sanitizzato in safeAction (no foto base64)
    onlineFlag?: boolean;
    userAgent?: string;
    platform: 'web';
    appVersion?: string;
    tsClient: string;              // ISO della cattura lato client
}

let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let intervalStarted = false;

function readQueue(): ClientErrorEntry[] {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : [];
    } catch {
        return [];
    }
}

function writeQueue(q: ClientErrorEntry[]): void {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(q));
    } catch {
        // QuotaExceeded: meglio perdere i log che bloccare la UI.
        try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
    }
}

function generateAttemptId(): string {
    // Fallback timestamp+random dove crypto.randomUUID() non c'è.
    try {
        if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
    } catch { /* ignore */ }
    return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function ensureFlushSchedulers() {
    if (intervalStarted) return;
    intervalStarted = true;
    // Periodico
    setInterval(() => { flushClientErrors().catch(() => { /* ignore */ }); }, FLUSH_INTERVAL_MS);
    // Al ritorno online
    if (typeof window !== 'undefined') {
        window.addEventListener('online', () => { flushClientErrors().catch(() => { /* ignore */ }); });
        // Invia subito le entry rimaste dalla sessione precedente
        setTimeout(() => { flushClientErrors().catch(() => { /* ignore */ }); }, 2_000);
    }
}

export function captureClientError(partial: Omit<ClientErrorEntry, 'platform' | 'tsClient' | 'userAgent' | 'onlineFlag' | 'appVersion'> & {
    onlineFlag?: boolean;
}): void {
    ensureFlushSchedulers();
    const entry: ClientErrorEntry = {
        ...partial,
        platform: 'web',
        tsClient: new Date().toISOString(),
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
        onlineFlag: partial.onlineFlag ?? (typeof navigator !== 'undefined' ? navigator.onLine : undefined),
        appVersion: (typeof process !== 'undefined' && process.env?.REACT_APP_VERSION) || undefined,
    };
    const q = readQueue();
    q.push(entry);
    // Coda limitata: scarta le più vecchie
    while (q.length > MAX_QUEUE_SIZE) q.shift();
    writeQueue(q);
    scheduleFlush();
}

function scheduleFlush() {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
        debounceTimer = null;
        flushClientErrors().catch(() => { /* ignore */ });
    }, FLUSH_DEBOUNCE_MS);
}

export async function flushClientErrors(): Promise<void> {
    const q = readQueue();
    if (q.length === 0) return;
    // navigator.onLine non è affidabile: si tenta sempre la POST e in caso
    // di errore si rimette tutto in coda.

    // Svuota la coda su una copia; se l'invio fallisce la ripristina unendola
    // alle entry arrivate nel frattempo.
    writeQueue([]);
    try {
        await api.post('/client-errors/batch', q);
    } catch {
        // Rimette in coda (max 100), le entry nuove hanno priorità
        const fresh = readQueue();
        const merged = [...q, ...fresh];
        while (merged.length > MAX_QUEUE_SIZE) merged.shift();
        writeQueue(merged);
    }
}

export const clientErrorLogger = {
    capture: captureClientError,
    flush: flushClientErrors,
    generateAttemptId,
};
